import { CartAddEvent } from '@theme/events';

const ADDED_STATE_MS = 1500;
const HIGHLIGHT_MS = 1200;
const SHEET_CLOSE_DRAG_PX = 80;
const CART_ADDING = 'data-ejanse-adding';

class EjanseBuyBox extends HTMLElement {
  #abort = new AbortController();
  #added = false;

  connectedCallback() {
    const signal = this.#abort.signal;
    this.sheet = this.querySelector('[data-ejanse-size-guide]');
    this.notifySheet = this.querySelector('[data-ejanse-notify]');
    this.querySelectorAll('[data-ejanse-open-size-guide]').forEach((link) =>
      link.addEventListener('click', () => this.openSizeGuide(), { signal })
    );
    for (const sheet of [this.sheet, this.notifySheet]) if (sheet) this.#setUpSheet(sheet, signal);

    if (this.isLive) {
      this.sizes = [...this.querySelectorAll('[data-ejanse-size]')];
      this.sizeRow = this.querySelector('[data-ejanse-sizes]');
      this.form = this.querySelector('.ejanse-buy-box__form');
      this.idInput = this.form.querySelector('input[name="id"]');
      this.button = this.querySelector('[data-ejanse-main-button]');
      this.stock = this.querySelector('[data-ejanse-stock]');
      this.anchor = this.button;
      this.buyNowButton = this.querySelector('[data-ejanse-buy-now]');
      this.sizes.forEach((size) => size.addEventListener('click', () => this.select(size), { signal }));
      this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
      this.buyNowButton?.addEventListener('click', () => this.addToCart(this.selected, { goToCheckout: true }), { signal });
    } else {
      this.sizes = [];
      this.anchor = this.querySelector('[data-ejanse-drop-form]');
    }

    const stickyElement = this.querySelector('[data-ejanse-sticky]');
    if (stickyElement && this.anchor) this.sticky = new StickyBar(stickyElement, this);

    this.#render();
  }

  get isLive() {
    return this.dataset.dropState === 'live';
  }

  focusDropForm() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.anchor.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    this.anchor.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.sticky?.destroy();
  }

  get selected() {
    return this.sizes.find((size) => size.getAttribute('aria-checked') === 'true') ?? null;
  }

  get productAvailable() {
    return this.dataset.productAvailable === 'true';
  }

  select(size) {
    this.sizes.forEach((s) => s.setAttribute('aria-checked', String(s === size)));
    this.idInput.value = size.dataset.variantId;
    this.#render();
    this.dispatchEvent(
      new CustomEvent('ejanse:size-change', {
        bubbles: true,
        detail: { variantId: size.dataset.variantId, size: size.dataset.size, available: size.dataset.available === 'true' },
      })
    );
  }

  selectByVariantId(variantId) {
    const size = this.sizes.find((s) => s.dataset.variantId === variantId);
    if (size) this.select(size);
    return size ?? null;
  }

  openSizeGuide() {
    this.sheet?.showModal();
  }

  promptSize() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.sizeRow.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    this.sizes[0]?.focus({ preventScroll: true });
    this.sizeRow.classList.add('is-highlighted');
    setTimeout(() => this.sizeRow.classList.remove('is-highlighted'), HIGHLIGHT_MS);
  }

  requestNotify(size = this.selected) {
    if (!size || !this.notifySheet) {
      if (!this.isLive) this.focusDropForm();
      return;
    }

    const label = size.dataset.size;
    const body = this.notifySheet.querySelector('[data-notify-heading-template]');
    const heading = this.notifySheet.querySelector('[data-ejanse-email-heading]');
    if (heading) heading.textContent = body.dataset.notifyHeadingTemplate.replace('[size]', label);
    this.notifySheet.querySelector('[data-ejanse-email-tags]').value = `newsletter, notify-${label.toLowerCase()}`;
    this.notifySheet.querySelector('ejanse-email-form')?.reset?.();
    this.notifySheet.showModal();
    this.notifySheet.querySelector('input[type="email"]')?.focus();
  }

  /** Label for the sticky bar, which also shows size and price. */
  get stickyLabel() {
    const text = this.dataset;
    const size = this.selected;
    if (this.dataset.dropState === 'pre-drop') return text.textStickyPreDrop;
    if (this.dataset.dropState === 'sold-out') return text.textStickySoldOut;
    if (this.#added) return text.textAdded;
    if (!size) return `${text.textChoose} · ${text.price}`;
    if (size.dataset.available !== 'true') return `${text.textNotify} · ${size.dataset.size}`;
    return `${text.textAdd} · ${size.dataset.size} · ${text.price}`;
  }

  #render() {
    this.sticky?.render();
    if (!this.isLive) return;

    const size = this.selected;
    const text = this.dataset;
    const canBuy = size?.dataset.available === 'true';
    this.toggleAttribute('data-can-buy', canBuy);

    if (this.#added) {
      this.button.textContent = text.textAdded;
      return;
    }

    if (!size) {
      this.button.textContent = this.productAvailable ? text.textChoose : text.textNotify;
      this.#setStock('', '');
      return;
    }

    if (!canBuy) {
      this.button.textContent = text.textNotify;
      this.#setStock('sold-out', text.textSizeSoldOut.replace('[size]', size.dataset.size));
      return;
    }

    const quantity = Number(size.dataset.quantity);
    const isLow = size.dataset.tracked === 'true' && quantity >= 1 && quantity <= 5;
    this.button.textContent = text.textAdd;
    this.#setStock(
      isLow ? 'low' : 'in',
      isLow ? text.textLowStock.replace('[quantity]', quantity).replace('[size]', size.dataset.size) : text.textInStock
    );
  }

  #setStock(state, message) {
    this.stock.dataset.state = state;
    this.stock.textContent = message;
  }

  #onSubmit(event) {
    event.preventDefault();
    const size = this.selected;

    if (!size) {
      if (this.productAvailable) this.promptSize();
      else this.requestNotify(null);
      return;
    }

    if (size.dataset.available !== 'true') {
      this.requestNotify(size);
      return;
    }

    this.addToCart(size);
  }

  #setBusy(busy) {
    for (const button of [this.button, this.sticky?.button, this.buyNowButton]) {
      if (!button) continue;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  }

  async addToCart(size = this.selected, { goToCheckout = false } = {}) {
    if (!size || size.dataset.available !== 'true' || this.button.getAttribute('aria-busy') === 'true') return;
    this.#setBusy(true);

    const root = window.Shopify?.routes?.root ?? '/';
    const body = { items: [{ id: Number(size.dataset.variantId), quantity: 1 }] };
    // The cart HTML comes back with the add, in one request. Straight to checkout needs none, so that request stays light.
    if (!goToCheckout) {
      body.sections = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
        .filter(Boolean)
        .join(',');
    }
    const earlyCart = goToCheckout ? null : this.#openCartEarly();
    let failed = true;

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();

      if (!response.ok) {
        this.#setStock('error', result.description || result.message || '');
        return;
      }

      failed = false;
      if (goToCheckout) {
        window.location.href = `${root}checkout`;
        return;
      }

      this.#added = true;
      this.#render();
      setTimeout(() => {
        this.#added = false;
        this.#render();
      }, ADDED_STATE_MS);

      // The new total is in the cart HTML already, so no second request for the cart is needed.
      const cartCount = this.#cartCountFrom(result.sections);
      this.dispatchEvent(
        new CartAddEvent(cartCount === null ? undefined : { item_count: cartCount }, size.dataset.variantId, {
          source: 'ejanse-buy-box',
          itemCount: cartCount ?? this.#shownCartCount + 1,
          productId: this.dataset.productId,
          variantId: size.dataset.variantId,
          sections: result.sections,
        })
      );
      if (earlyCart && !earlyCart.dialog.contains(document.activeElement)) {
        earlyCart.dialog.querySelector('.cart-drawer__close-button')?.focus({ preventScroll: true });
      }
    } catch (error) {
      console.error(error);
    } finally {
      earlyCart?.dialog.removeAttribute(CART_ADDING);
      // Nothing was added: close the cart again so the message under the sizes can be seen.
      if (failed) earlyCart?.drawer.closeDialog();
      this.#setBusy(false);
    }
  }

  /**
   * Opens the cart the moment the button is pressed, in a waiting state (styled in ejanse.css),
   * so the tap is answered at once instead of after the server replies.
   * Only when the theme setting "open cart after adding" is on and the cart script is ready.
   */
  #openCartEarly() {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    const dialog = drawer?.querySelector('dialog');
    if (!dialog || dialog.open || !drawer.hasAttribute('auto-open') || typeof drawer.showDialog !== 'function') return null;
    dialog.setAttribute(CART_ADDING, '');
    drawer.showDialog();
    return { drawer, dialog };
  }

  /** Total number of items, read from the cart HTML the add request returned. */
  #cartCountFrom(sections) {
    for (const html of Object.values(sections ?? {})) {
      const match = /ref="cartItemCount"[^>]*>\s*(\d+)/.exec(String(html));
      if (match) return Number(match[1]);
    }
    return null;
  }

  get #shownCartCount() {
    return parseInt(document.querySelector('cart-icon .cart-bubble__text-count')?.textContent ?? '', 10) || 0;
  }

  #setUpSheet(sheet, signal) {
    sheet.querySelector('[data-ejanse-close-sheet]')?.addEventListener('click', () => sheet.close(), { signal });
    sheet.addEventListener(
      'click',
      (event) => {
        if (event.target === sheet) sheet.close();
      },
      { signal }
    );

    let startY = null;
    let delta = 0;
    sheet.addEventListener(
      'touchstart',
      (event) => {
        startY = sheet.scrollTop <= 0 ? event.touches[0].clientY : null;
        delta = 0;
      },
      { signal, passive: true }
    );
    sheet.addEventListener(
      'touchmove',
      (event) => {
        if (startY === null) return;
        delta = Math.max(0, event.touches[0].clientY - startY);
        sheet.classList.add('is-dragging');
        sheet.style.transform = `translateY(${delta}px)`;
      },
      { signal, passive: true }
    );
    sheet.addEventListener(
      'touchend',
      () => {
        if (startY === null) return;
        sheet.classList.remove('is-dragging');
        sheet.style.transform = '';
        if (delta > SHEET_CLOSE_DRAG_PX) sheet.close();
        startY = null;
      },
      { signal }
    );
  }
}

class StickyBar {
  #abort = new AbortController();
  #mainVisible = true;
  #footerVisible = false;
  #inputFocused = false;

  constructor(element, buyBox) {
    this.element = element;
    this.buyBox = buyBox;
    this.button = element.querySelector('[data-ejanse-sticky-button]');
    this.sizesPanel = element.querySelector('.ejanse-sticky__sizes');
    this.pills = [...element.querySelectorAll('[data-ejanse-sticky-size]')];

    // Fixed positioning breaks inside transformed ancestors, so the bar lives at the end of <body>.
    document.body.append(element);

    const signal = this.#abort.signal;
    this.button.addEventListener('click', () => this.#onButtonClick(), { signal });
    this.pills.forEach((pill) => pill.addEventListener('click', () => this.#onPillClick(pill), { signal }));
    element
      .querySelector('[data-ejanse-open-size-guide]')
      ?.addEventListener('click', () => buyBox.openSizeGuide(), { signal });

    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.collapse(), { signal });
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!element.contains(event.target) && !buyBox.sheet?.open && !buyBox.notifySheet?.open) this.collapse();
      },
      { signal }
    );
    const checkTyping = () => {
      const active = document.activeElement;
      this.#inputFocused =
        active instanceof Element &&
        active.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select') &&
        !element.contains(active);
      this.#updateVisibility();
    };
    document.addEventListener('focusin', checkTyping, { signal });
    document.addEventListener('focusout', () => setTimeout(checkTyping), { signal });

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === buyBox.anchor) this.#mainVisible = entry.isIntersecting;
        else this.#footerVisible = entry.isIntersecting;
      }
      this.#updateVisibility();
    });
    this.observer.observe(buyBox.anchor);
    // Only the footer's last row would sit under the bar; its signup already hides the bar via focus.
    const footer = document.querySelector('footer');
    const footerEnd = footer?.lastElementChild ?? footer;
    if (footerEnd) this.observer.observe(footerEnd);

    this.render();
  }

  render() {
    this.button.textContent = this.buyBox.stickyLabel;
  }

  expand() {
    this.element.toggleAttribute('data-expanded', true);
    this.sizesPanel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.pills.find((pill) => !pill.classList.contains('is-sold-out'))?.focus();
  }

  collapse() {
    if (!this.element.hasAttribute('data-expanded')) return;
    const hadFocus = this.element.contains(document.activeElement);
    this.element.removeAttribute('data-expanded');
    this.sizesPanel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (hadFocus && this.#isVisible) this.button.focus();
  }

  get #isVisible() {
    return !this.#mainVisible && !this.#footerVisible && !this.#inputFocused;
  }

  #updateVisibility() {
    const visible = this.#isVisible;
    this.element.toggleAttribute('data-visible', visible);
    this.element.inert = !visible;
    if (!visible) this.collapse();
  }

  #onButtonClick() {
    const size = this.buyBox.selected;
    if (!this.buyBox.isLive) {
      this.buyBox.focusDropForm();
    } else if (!size) {
      this.expand();
    } else if (size.dataset.available !== 'true') {
      this.buyBox.requestNotify(size);
    } else {
      this.buyBox.addToCart(size);
    }
  }

  #onPillClick(pill) {
    const size = this.buyBox.selectByVariantId(pill.dataset.ejanseStickySize);
    this.collapse();
    if (!size) return;
    if (size.dataset.available === 'true') this.buyBox.addToCart(size);
    else this.buyBox.requestNotify(size);
  }

  destroy() {
    this.#abort.abort();
    this.observer.disconnect();
    this.element.remove();
  }
}

if (!customElements.get('ejanse-buy-box')) customElements.define('ejanse-buy-box', EjanseBuyBox);
